import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Modal, Button, Upload, message, Space } from 'antd'
import { AudioOutlined, LoadingOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons'
import { connect, useDispatch } from 'react-redux'
import actions from '../../Redux/FileUpload/action'
import { infoMessage } from 'api/global'

interface AudioUploadProps {
  audioIds: string[]
  setAudioIds: React.Dispatch<React.SetStateAction<string[]>>
  fileData: any
  multipleFileData: any[]
  loading: boolean
  multiple?: boolean
  soruceType?: string
}

const AudioUpload: React.FC<AudioUploadProps> = ({
  audioIds,
  setAudioIds,
  fileData,
  multipleFileData,
  loading,
  multiple = false,
  soruceType = 'other'
}) => {
  const dispatch = useDispatch()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [recordedAudio, setRecordedAudio] = useState<string | null>(null)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [recordedTime, setRecordedTime] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (fileData?._id) {
      setAudioIds([...audioIds, fileData._id])
      dispatch({
        type: actions.CLEAR_FILE_DATA,
      })
    }
  }, [fileData])

  useEffect(() => {
    if (multipleFileData.length > 0) {
      const ids = multipleFileData.map((file: any) => file._id)
      setAudioIds((prev: string[]) =>
        Array.from(new Set([...prev, ...ids]))
      )
      dispatch({
        type: actions.CLEAR_FILE_DATA,
      })
    }
  }, [multipleFileData])

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder
      audioChunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data)
      }

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' })
        const audioUrl = URL.createObjectURL(audioBlob)
        setRecordedAudio(audioUrl)
        setAudioBlob(audioBlob)
        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorder.start()
      setIsRecording(true)
      setRecordedTime(0)

      timerRef.current = setInterval(() => {
        setRecordedTime(prev => prev + 1)
      }, 1000)

    } catch (error) {
      console.error('Error accessing microphone:', error)
      message.error('Unable to access microphone. Please check permissions.')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }

  const uploadRecordedAudio = async () => {
    if (!audioBlob) return

    try {
      dispatch({
        type: actions.CHANGE_FILE_TYPE,
      })

      const fileObj = new FormData()
      const fileName = `recording_${Date.now()}.wav`
      const audioFile = new File([audioBlob], fileName, { type: 'audio/wav' })
      fileObj.append('file', audioFile)

      const newFile = {
        title: fileName,
        description: 'audio_recording',
        altText: 'audio',
        sourceType: soruceType,
      }

      dispatch({
        type: actions.REQUEST_FILE_UPLOAD,
        payload: fileObj,
        newFile: newFile,
      })

      closeModal()
    } catch (error) {
      console.error('Error uploading audio:', error)
      infoMessage('Something went wrong while uploading audio')
    }
  }

  const handleGalleryUpload = async (info: any) => {
    if (!info.fileList || info.fileList.length === 0) return;

    try {
      dispatch({ type: actions.CHANGE_FILE_TYPE });

      // If multiple files
      info.fileList.forEach((file: any) => {
        const fileObj = new FormData();

        // Make sure originFileObj exists
        if (!file.originFileObj) {
          console.error("originFileObj missing for file", file);
          return;
        }

        fileObj.append("file", file.originFileObj);

        const newFile = {
          title: file.name,
          description: "audio_file",
          altText: "audio",
          sourceType: soruceType,
        };

        dispatch({
          type: actions.REQUEST_FILE_UPLOAD,
          payload: fileObj,
          newFile: newFile,
        });
      });
    } catch (error) {
      console.error("Error uploading audio:", error);
      infoMessage("Something went wrong while uploading audio");
    }
  };
  const deleteRecordedAudio = () => {
    setRecordedAudio(null)
    setAudioBlob(null)
    setRecordedTime(0)
  }

  const closeModal = useCallback(() => {
    setIsModalVisible(false)
    setRecordedAudio(null)
    setAudioBlob(null)
    setRecordedTime(0)
    setIsRecording(false)
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }
  }, [])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <>
      <Button
        icon={<AudioOutlined />}
        className="media-button"
        onClick={() => setIsModalVisible(true)}
      >
        Audio
      </Button>

      <Modal
        open={isModalVisible}
        onCancel={closeModal}
        footer={null}
        width={450}
        centered
        bodyStyle={{ padding: '30px 20px' }}
        closable={false}
      >
        {/* Header */}
        <div className="flex justify-between items-center" style={{ marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Upload Audio</h2>
          <CloseOutlined onClick={closeModal} style={{ fontSize: '1.4rem', cursor: 'pointer' }} />
        </div>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Recording Option */}
          <Button
            type={isRecording ? 'default' : 'primary'}
            danger={isRecording}
            size="large"
            icon={isRecording ? <LoadingOutlined /> : <AudioOutlined />}
            onClick={isRecording ? stopRecording : startRecording}
            style={{
              width: '100%',
              height: '55px',
              borderRadius: '8px',
              fontWeight: 500,
              fontSize: '1rem',
            }}
          >
            {isRecording ? `Stop Recording (${formatTime(recordedTime)})` : 'Record Audio'}
          </Button>

          {/* Recorded Audio Preview */}
          {recordedAudio && (
            <div className="audio-preview" style={{ textAlign: 'center' }}>
              <audio controls style={{ width: '100%', borderRadius: '8px', marginBottom: '12px' }}>
                <source src={recordedAudio} type="audio/wav" />
                Your browser does not support the audio element.
              </audio>
              <Space size="middle">
                <Button
                  type="primary"
                  onClick={uploadRecordedAudio}
                  loading={loading}
                  style={{ minWidth: '120px' }}
                >
                  Upload
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={deleteRecordedAudio}
                  style={{ minWidth: '120px' }}
                >
                  Delete
                </Button>
              </Space>
            </div>
          )}

          {/* Divider */}
          <div style={{ textAlign: 'center', color: '#999', fontWeight: 500, margin: '10px 0' }}>OR</div>

          {/* Gallery Upload */}
          <Upload
            accept=".mp3,.wav,.ogg,.m4a"
            showUploadList={false}
            onChange={handleGalleryUpload}
            beforeUpload={() => false}
          >
            <Button
              size="large"
              icon={<AudioOutlined />}
              loading={loading}
              style={{
                width: '100%',
                height: '55px',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '1rem',
              }}
            >
              Choose from Gallery
            </Button>
          </Upload>
        </Space>
      </Modal>

    </>
  )
}

const mapStateToProps = (state: any) => ({
  fileData: state.fileUpload.fileData,
  multipleFileData: state.fileUpload.multipleFileData,
  loading: state.fileUpload.loading,
})

export default connect(mapStateToProps)(AudioUpload)
